// =====================================================
// Swiss Finance OS - Supabase Client Configuration
// Multi-tenant, Swiss compliance ready client
// =====================================================

import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

type UserRow = Database['public']['Tables']['users']['Row'];
type OrganizationRow = Database['public']['Tables']['organizations']['Row'];
type AuditLogInsert = Database['public']['Tables']['audit_logs']['Insert'];
type UserProfileWithOrganization = UserRow & { organizations: OrganizationRow | null };

export const SUPABASE_URL = "https://rnskttodnajvitfqjpcn.supabase.co";
export const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJuc2t0dG9kbmFqdml0ZnFqcGNuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY3NTA2NDcsImV4cCI6MjA3MjMyNjY0N30.jJlR9tEIwiH-Z09IvIrhen42nLjXT_MUxJvQ8CZa-eI";

// Create Swiss Finance OS optimized client
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: typeof window !== 'undefined' ? localStorage : undefined,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // Optimize for financial data updates
    },
  },
  global: {
    headers: {
      'X-Swiss-Finance-OS': '1.0',
      'X-Client-Info': 'Swiss Finance OS/1.0',
    },
  },
});

// =====================================================
// Swiss Finance OS Helper Functions
// =====================================================

/**
 * Get current user's organization context
 * Uses the actual `users` ↔ `organizations` relationship.
 */
export const getCurrentUserOrganization = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Use array query to avoid 406 errors for new users without profiles
  const { data: profileArray, error: profileError } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .returns<UserProfileWithOrganization[]>();

  if (profileError) {
    console.warn('Unable to load user profile:', profileError?.message);
    return null;
  }

  // Handle new users who don't have a profile yet
  if (!profileArray || profileArray.length === 0) {
    console.log('No profile found for user - this is OK for new users going through onboarding');
    return null;
  }

  const profile = profileArray[0];
  if (!profile) {
    console.warn('Unable to load user profile: profile is null');
    return null;
  }

  // Fetch organization separately if user has one
  let organization: OrganizationRow | null = null;
  if (profile?.organization_id) {
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('id, name, slug, swiss_business_id, address, contact_info, subscription_tier, settings, created_at, updated_at')
      .eq('id', profile.organization_id)
      .maybeSingle();

    if (orgError) {
      console.warn('Unable to fetch organization (RLS may be blocking):', orgError.message);
      // Return profile without organization during onboarding
      return profile ? {
        ...profile,
        company_id: profile.organization_id || '',
        company: null,
        organization: null,
        organizations: null,
      } : null;
    }

    if (!orgData) {
      console.warn('Organization not found for organization_id:', profile.organization_id);
      return null;
    }

    organization = orgData;
  }

  if (!organization) {
    console.warn('Authenticated user missing organization relation');
    return null;
  }

  return profile && organization ? {
    ...profile,
    company_id: organization.id,
    company: organization,
    organization,
    organizations: organization, // Keep compatibility
  } : null;
};

/**
 * Get user's accessible mandates
 * Based on role and assignments
 */
export const getUserMandates = async (userId: string) => {
  const { data, error } = await supabase
    .from('mandates')
    .select('*')
    .contains('assigned_users', [userId])
    .eq('status', 'active');

  if (error) {
    console.warn('Error loading mandates for user:', error.message);
  }

  return data || [];
};

/**
 * Create audit log entry
 * Swiss compliance requirement for all data changes
 * Uses secure database function to prevent audit log manipulation
 */
export const createAuditLog = async (
  tableName: string,
  recordId: string,
  action: 'INSERT' | 'UPDATE' | 'DELETE' | 'VIEW',
  oldValues?: AuditLogInsert['old_values'],
  newValues?: AuditLogInsert['new_values']
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Use secure database function instead of direct insert
    await supabase.rpc('insert_audit_log', {
      p_action: action,
      p_table_name: tableName,
      p_record_id: recordId,
      p_old_values: oldValues || null,
      p_new_values: newValues || null,
      p_ip_address: null,
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent ?? null : null,
    });
  } catch (error) {
    console.error('Failed to create audit log:', error);
  }
};

/**
 * Swiss VAT rate helper
 * Standard Swiss VAT rates with validation
 */
export const SWISS_VAT_RATES = {
  STANDARD: 7.7,
  REDUCED_1: 2.5, // Food, books, newspapers, medicine
  REDUCED_2: 3.7, // Accommodation services
  EXEMPT: 0.0,    // Exports, medical, education
} as const;

export type SwissVATRate = typeof SWISS_VAT_RATES[keyof typeof SWISS_VAT_RATES];

/**
 * Format Swiss currency
 * Consistent CHF formatting across the app
 */
export const formatSwissCurrency = (
  amount: number,
  options: {
    showSign?: boolean;
    precision?: number;
    compact?: boolean;
  } = {}
): string => {
  const { showSign = false, precision = 2, compact = false } = options;

  const formatter = new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    notation: compact ? 'compact' : 'standard',
  });

  const formatted = formatter.format(Math.abs(amount));

  if (showSign && amount !== 0) {
    return amount > 0 ? `+${formatted}` : `-${formatted}`;
  }

  return amount < 0 ? `-${formatted}` : formatted;
};

/**
 * Swiss Chart of Accounts helper
 * Get account class name in German
 */
export const getAccountClassName = (accountClass: number): string => {
  const classNames = {
    1: 'Aktiven',
    2: 'Passiven',
    3: 'Betriebsertrag',
    4: 'Materialaufwand',
    5: 'Personalaufwand',
    6: 'Übriger Betriebsaufwand',
    7: 'Finanzergebnis',
    8: 'Abschreibungen/Reserven',
  };

  return classNames[accountClass as keyof typeof classNames] || 'Unbekannt';
};

/**
 * Transaction confidence level helper
 * Convert numeric score to human-readable level
 */
export const getConfidenceLevel = (score: number): 'low' | 'medium' | 'high' => {
  if (score >= 90) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
};

/**
 * Swiss business validation
 * Validate Swiss UID format
 */
export const validateSwissUID = (uid: string): boolean => {
  // Swiss UID format: CHE-XXX.XXX.XXX
  const uidRegex = /^CHE-\d{3}\.\d{3}\.\d{3}$/;
  return uidRegex.test(uid);
};

// Export for use in components
export default supabase;
